import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import HttpIcon from '@material-ui/icons/Http';
import SearchIcon from '@material-ui/icons/Search';
import {
  CatalogIcon,
  Content,
  DocsIcon,
  useSidebarPinState,
} from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { PageBlueprint } from '@backstage/frontend-plugin-api';
import { CatalogSearchResultListItem } from '@backstage/plugin-catalog';
import {
  catalogApiRef,
  CATALOG_FILTER_EXISTS,
} from '@backstage/plugin-catalog-react';
import {
  SearchBar,
  SearchContextProvider,
  SearchFilter,
  SearchPagination,
  SearchResult,
  SearchResultPager,
  useSearch,
} from '@backstage/plugin-search-react';
import { SearchType } from '@backstage/plugin-search';
import searchPlugin from '@backstage/plugin-search/alpha';
import { TechDocsSearchResultListItem } from '@backstage/plugin-techdocs';
import { OpenApiSearchResultListItemExtension } from './OpenApiSearchResultListItem';
import { PreSearchEmptyState } from './PreSearchEmptyState';

const useSearchPageStyles = makeStyles(theme => ({
  filter: {
    '& + &': {
      marginTop: theme.spacing(2.5),
    },
  },
  filters: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

// Stand-in for the plugin-internal UrlUpdater (not exported). Keeps the search
// state in sync with the URL both ways so deep links from the search modal
// (/search?query=...) still work and searches remain shareable/back-button
// friendly. Uses the same qs bracket format as the stock implementation.
const UrlSync = () => {
  const location = useLocation();
  const {
    term,
    setTerm,
    types,
    setTypes,
    pageCursor,
    setPageCursor,
    filters,
    setFilters,
  } = useSearch();
  const lastSeenSearch = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (location.search === lastSeenSearch.current) {
      return;
    }
    lastSeenSearch.current = location.search;
    const params = new URLSearchParams(location.search);
    const urlFilters: Record<string, string | string[]> = {};
    params.forEach((value, key) => {
      const match = key.match(/^filters\[([^\]]+)\](\[\])?$/);
      if (!match) {
        return;
      }
      const [, name, isArray] = match;
      if (isArray) {
        urlFilters[name] = [...((urlFilters[name] as string[]) ?? []), value];
      } else {
        urlFilters[name] = value;
      }
    });
    if (Object.keys(urlFilters).length > 0) {
      setFilters(urlFilters);
    }
    const query = params.get('query');
    if (query) {
      setTerm(query);
    }
    const pageCursorParam = params.get('pageCursor');
    if (pageCursorParam) {
      setPageCursor(pageCursorParam);
    }
    setTypes(params.getAll('types[]'));
  }, [location, setTerm, setTypes, setPageCursor, setFilters]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('query', term);
    types.forEach(type => params.append('types[]', type));
    if (pageCursor) {
      params.set('pageCursor', pageCursor);
    }
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => params.append(`filters[${key}][]`, String(v)));
      } else if (value !== undefined && value !== null && value !== '') {
        params.set(`filters[${key}]`, String(value));
      }
    });
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    lastSeenSearch.current = `?${params.toString()}`;
    window.history.replaceState(null, document.title, newUrl);
  }, [term, types, pageCursor, filters]);

  return null;
};

const SearchPageContent = () => {
  const classes = useSearchPageStyles();
  const { isMobile } = useSidebarPinState();
  const { term, types } = useSearch();
  const catalogApi = useApi(catalogApiRef);
  const configApi = useApi(configApiRef);

  return (
    <Content>
      <Grid container direction="row">
        <Grid item xs={12}>
          <SearchBar debounceTime={100} />
        </Grid>
        {!isMobile && (
          <Grid item xs={3}>
            <SearchType.Accordion
              name="Result Type"
              defaultValue={configApi.getOptionalString('search.defaultType')}
              showCounts
              types={[
                {
                  value: 'software-catalog',
                  name: 'Software Catalog',
                  icon: <CatalogIcon />,
                },
                {
                  value: 'openapi-endpoint',
                  name: 'API Endpoints',
                  icon: <HttpIcon />,
                },
                {
                  value: 'techdocs',
                  name: 'Documentation',
                  icon: <DocsIcon />,
                },
              ]}
            />
            <Paper className={classes.filters}>
              {types.includes('techdocs') && (
                <SearchFilter.Select
                  className={classes.filter}
                  label="Entity"
                  name="name"
                  values={async () => {
                    const { items } = await catalogApi.getEntities({
                      fields: ['metadata.name'],
                      filter: {
                        'metadata.annotations.backstage.io/techdocs-ref':
                          CATALOG_FILTER_EXISTS,
                      },
                    });
                    const names = items.map(entity => entity.metadata.name);
                    names.sort();
                    return names;
                  }}
                />
              )}
              <SearchFilter.Select
                className={classes.filter}
                label="Kind"
                name="kind"
                values={[
                  'API',
                  'Component',
                  'Domain',
                  'Group',
                  'Location',
                  'Resource',
                  'System',
                  'Template',
                  'User',
                ]}
              />
              <SearchFilter.Checkbox
                className={classes.filter}
                label="Lifecycle"
                name="lifecycle"
                values={['experimental', 'production']}
              />
            </Paper>
          </Grid>
        )}
        <Grid item xs>
          {term ? (
            <>
              <SearchPagination />
              <SearchResult>
                <CatalogSearchResultListItem icon={<CatalogIcon />} />
                <OpenApiSearchResultListItemExtension icon={<HttpIcon />} />
                <TechDocsSearchResultListItem icon={<DocsIcon />} />
              </SearchResult>
              <SearchResultPager />
            </>
          ) : (
            <PreSearchEmptyState />
          )}
        </Grid>
      </Grid>
    </Content>
  );
};

// Replaces the stock page:search extension (its loader can't be customized in
// place). Identical to the default search page except that with an empty query
// it shows PreSearchEmptyState instead of running an empty search that renders
// "Sorry, no results were found".
const searchPage = PageBlueprint.make({
  params: {
    path: '/search',
    routeRef: searchPlugin.routes.root,
    title: 'Search',
    icon: <SearchIcon fontSize="inherit" />,
    loader: async () => (
      <SearchContextProvider>
        <UrlSync />
        <SearchPageContent />
      </SearchContextProvider>
    ),
  },
});

export const searchPluginWithOverrides = searchPlugin.withOverrides({
  extensions: [searchPage],
});
